import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import { NIL } from "uuid"

import { Heading } from "@jobteaser/spark/components/Heading"

import { UnderlineShape } from "@/modules/brand/components/UnderlineShape/UnderlineShape"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { ComplianceModal } from "@/modules/jobAds/client/components/ComplianceModal/ComplianceModal"
import { ResultsSort } from "@/modules/jobAds/client/components/ResultsSort/ResultsSort"
import { usePrefilteredJobSearch } from "@/modules/jobSearchPrefiltered/hooks/usePrefilteredJobSearch"
import { useLocale } from "@/modules/locales/useLocale"
import { formatNumber } from "@/modules/number/formatNumber"

import styles from "./ResultsHeader.module.css"

type ResultsHeaderProps = {
  jobAdsCount: number
}

const DynamicResultsTitle = dynamic(() =>
  import("@/modules/jobSearchPrefiltered/components/ResultsTitle/ResultsTitle").then(module => module.ResultsTitle)
)

export const ResultsHeader: FunctionComponent<ResultsHeaderProps> = ({ jobAdsCount }) => {
  const { t } = useTranslation(["job_ads_fo", "shared_modal"])
  const locale = useLocale()
  const isPrefilteredJobSearchPage = usePrefilteredJobSearch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const isCareerCenter = dataCareerCenter?.id !== NIL

  return (
    <div className={styles.main}>
      <div className={styles.count}>
        <Heading as="h2" variant="title2">
          <UnderlineShape text={formatNumber(jobAdsCount, locale, false)} />{" "}
          {isPrefilteredJobSearchPage ? (
            <DynamicResultsTitle jobAdsCount={jobAdsCount} />
          ) : (
            t("job_ads_fo.client.search.results.header", {
              count: jobAdsCount,
            })
          )}
        </Heading>
        <ComplianceModal
          closeLabel={t("shared_modal.close")}
          title={t("job_ads_fo.client.search.results.compliance_modal.title")}
        >
          {isCareerCenter
            ? t("job_ads_fo.client.search.results.compliance_modal.career_center.detail")
            : t("job_ads_fo.client.search.results.compliance_modal.detail")}
        </ComplianceModal>
      </div>
      <ResultsSort
        label={t("job_ads_fo.client.search.results.sort_by")}
        recencyLabel={t("job_ads_fo.client.search.results.sort.recency")}
        relevanceLabel={t("job_ads_fo.client.search.results.sort.relevance")}
      />
    </div>
  )
}
